import { Component, DestroyRef, computed, effect, inject, signal, untracked } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatCard } from '@angular/material/card';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { FormBuilder, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { CustomValidators } from 'angular-custom-validators';
import { dataStoreSignal } from '../app.component';
import { valueToDate } from '../globals';
import {
  AddressData,
  AddressValidationFlowService,
} from '../services/address-validation-flow.service';
import {
  createEmployeePhotoDraft,
  EmployeeProfileService,
  getInitial,
} from '../services/employee-profile.service';

interface AccountProfile extends AddressData {
  id?: string;
  name?: string;
  phone?: string;
  photoUrl?: string;
  startDate?: unknown;
  vogDate?: unknown;
  driversTestDate?: unknown;
}

@Component({
  selector: 'app-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss'],
  imports: [
    MatButton,
    MatIcon,
    MatCard,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInput,
    MatTooltip,
    MatProgressSpinner,
  ],
})
export class AccountComponent {
  sendingReset = signal(false);
  savingContact = signal(false);
  savingPhoto = signal(false);
  contactError = signal('');
  photoError = signal('');
  imageFailed = signal(false);
  private readonly destroyRef = inject(DestroyRef);
  private readonly builder = inject(FormBuilder);
  private readonly addressService = inject(AddressValidationFlowService);
  private readonly profileService = inject(EmployeeProfileService);
  private readonly auth = getAuth();
  private readonly state = computed(() => dataStoreSignal());
  readonly profile = computed(() => this.state().orgUser as AccountProfile | null);
  readonly personalDataEnabled = computed(
    () => this.state().paidFunctionalities?.usesEmployeePersonalDataManagement === true,
  );
  readonly photoEnabled = computed(
    () => this.state().paidFunctionalities?.usesEmployeeListForStaff === true,
  );
  readonly photo = createEmployeePhotoDraft(this.destroyRef);
  readonly contactForm = this.builder.group({
    phone: ['', [Validators.required, CustomValidators.phone]],
  });
  address: ReturnType<AddressValidationFlowService['connect']> = this.createInactiveAddress();
  readonly getInitial = getInitial;
  private addressConnected = false;
  private context = '';
  private personalVisible = false;
  private photoVisible = false;
  private original: AccountProfile = {};

  private createInactiveAddress(): ReturnType<AddressValidationFlowService['connect']> {
    return {
      isLookingUp: signal(false),
      addressNotFound: signal(false),
      reset: () => undefined,
      disconnect: () => undefined,
    };
  }

  private syncAddressControls(enabled: boolean): void {
    if (enabled === this.addressConnected) return;

    if (enabled) {
      this.addressService.addControls(this.contactForm);
      this.address = this.addressService.connect(this.contactForm, this.destroyRef);
      this.addressConnected = true;
      return;
    }

    this.address.disconnect();
    const form = this.contactForm as unknown as UntypedFormGroup;
    ['postal', 'houseNumber', 'houseNumberAddition', 'streetName', 'city'].forEach((field) =>
      form.removeControl(field),
    );
    this.address = this.createInactiveAddress();
    this.addressConnected = false;
  }

  constructor(private snackBar: MatSnackBar) {
    effect(() => {
      const state = this.state();
      const profile = this.profile();
      const personalVisible = this.personalDataEnabled();
      const photoVisible = this.photoEnabled();
      const context = `${state.selectedOrganisation?.id ?? ''}/${profile?.id ?? ''}`;
      untracked(() => {
        const switched = context !== this.context;
        if (
          switched ||
          personalVisible !== this.personalVisible ||
          (!this.contactForm.dirty && !this.savingContact())
        ) {
          this.syncAddressControls(personalVisible);
          this.resetContact(profile ?? {});
        }
        if (
          switched ||
          photoVisible !== this.photoVisible ||
          (!this.photo.selectedPhoto() && !this.photo.photoRemoved() && !this.savingPhoto())
        ) {
          this.photo.reset(profile?.photoUrl ?? null);
          this.imageFailed.set(false);
          this.photoError.set('');
        }
        this.context = context;
        this.personalVisible = personalVisible;
        this.photoVisible = photoVisible;
      });
    });
  }

  resetContact(profile: AccountProfile = this.profile() ?? {}) {
    this.original = { ...profile };
    this.contactForm.reset({ ...profile, phone: profile.phone ?? '' }, { emitEvent: false });
    this.address.reset();
    this.contactForm.updateValueAndValidity({ emitEvent: false });
    this.contactForm.markAllAsTouched();
    this.contactError.set('');
  }

  displayDate(value: unknown): string {
    const date = valueToDate(value);
    return date && !Number.isNaN(date.getTime())
      ? new Intl.DateTimeFormat('nl-NL').format(date)
      : 'Onbekend';
  }

  canSaveContact(): boolean {
    const raw = this.contactForm.getRawValue() as AddressData & { phone: string };
    return (
      this.personalDataEnabled() &&
      this.contactForm.valid &&
      this.contactForm.dirty &&
      !this.savingContact() &&
      !this.address.isLookingUp() &&
      [raw.phone, raw.postal, raw.houseNumber, raw.streetName, raw.city].every(
        (value) => String(value ?? '').trim().length > 0,
      )
    );
  }

  canSaveChanges(): boolean {
    const contactPending = this.personalDataEnabled() && this.contactForm.dirty;
    const photoPending =
      this.photoEnabled() && (!!this.photo.selectedPhoto() || this.photo.photoRemoved());
    return (
      (contactPending || photoPending) &&
      !this.savingContact() &&
      !this.savingPhoto() &&
      !this.address.isLookingUp() &&
      !(photoPending && this.photo.photoValidationError())
    );
  }

  private ownContext(): { organisationId: string; employeeId: string } | null {
    const organisationId = this.state().selectedOrganisation?.id;
    const employeeId = this.auth.currentUser?.uid;
    return organisationId && employeeId && this.profile()?.id === employeeId
      ? { organisationId, employeeId }
      : null;
  }

  async saveChanges(): Promise<void> {
    const contactPending = this.personalDataEnabled() && this.contactForm.dirty;
    const photoPending =
      this.photoEnabled() && (!!this.photo.selectedPhoto() || this.photo.photoRemoved());
    if (!contactPending && !photoPending) return;

    if (contactPending) {
      this.contactForm.markAllAsTouched();
      if (!this.canSaveContact()) {
        return;
      }
    }
    if (photoPending && this.photo.photoValidationError()) return;

    const context = this.ownContext();
    if (!context) return;
    const key = this.context;
    const contact = this.contactForm.getRawValue() as AddressData & { phone: string };
    let resolvedContact = contact;
    this.savingContact.set(contactPending);
    this.savingPhoto.set(photoPending);
    this.contactError.set('');
    this.photoError.set('');
    let contactResult: { changed: boolean; auditLogged?: boolean } | undefined;
    let photoResult:
      | { changed: boolean; photoUrl?: string | null; auditLogged?: boolean }
      | undefined;
    try {
      if (contactPending) {
        const resolvedAddress = await this.addressService.resolveAddress(
          contact,
          this.original,
          false,
          true,
        );
        if (!resolvedAddress) return;
        resolvedContact = { ...contact, ...resolvedAddress };
      }
      if (key !== this.context) return;
      if (contactPending && !this.personalDataEnabled()) return;
      if (photoPending && !this.photoEnabled()) return;

      if (contactPending) {
        try {
          contactResult = await this.profileService.saveContact(
            context.organisationId,
            resolvedContact,
          );
          if (key !== this.context) return;
          this.original = { ...this.original, ...resolvedContact };
          this.contactForm.markAsPristine();
        } catch {
          if (key === this.context)
            this.contactError.set(
              'Uw gegevens konden niet worden opgeslagen. Controleer de gegevens en probeer het opnieuw.',
            );
          return;
        }
      }

      if (photoPending) {
        try {
          photoResult = await this.profileService.savePhoto(
            context.organisationId,
            context.employeeId,
            this.photo.photoRemoved() ? null : this.photo.selectedPhoto(),
          );
          if (key !== this.context) return;
          this.photo.reset(photoResult.photoUrl ?? null);
        } catch {
          if (key === this.context) {
            this.photoError.set('Uw profielfoto kon niet worden opgeslagen. Probeer het opnieuw.');
            if (contactResult?.changed) {
              this.snackBar.open(
                'Uw gegevens zijn opgeslagen. De profielfoto kon niet worden opgeslagen.',
                'X',
                { duration: 5000 },
              );
            }
          }
          return;
        }
      }

      const savedContact = contactResult !== undefined;
      const savedPhoto = photoResult !== undefined;
      if (savedContact || savedPhoto) {
        const auditFailed =
          contactResult?.auditLogged === false || photoResult?.auditLogged === false;
        const savedItems =
          savedContact && savedPhoto
            ? 'Uw gegevens en profielfoto zijn opgeslagen'
            : savedPhoto
              ? 'Uw profielfoto is opgeslagen'
              : 'Uw gegevens zijn opgeslagen';
        this.snackBar.open(
          auditFailed
            ? `${savedItems}, maar de wijzigingslog kon niet worden opgeslagen.`
            : `${savedItems}.`,
          'X',
          { duration: 5000 },
        );
      }
    } catch {
      if (key === this.context && contactPending) {
        this.contactError.set('Het adres kon niet worden gecontroleerd. Probeer het opnieuw.');
      }
    } finally {
      this.savingContact.set(false);
      this.savingPhoto.set(false);
    }
  }

  selectPhoto(event: Event) {
    if (!this.photoEnabled() || this.savingPhoto()) return;
    this.photo.select(event);
    this.photoError.set('');
    this.imageFailed.set(false);
  }

  async sendPasswordReset(): Promise<void> {
    const authInstance = this.auth;
    const user = authInstance.currentUser;
    const email = user?.email;
    if (!email || !user.providerData.some((provider) => provider.providerId === 'password')) {
      this.snackBar.open(
        'Wachtwoord opnieuw instellen is niet beschikbaar voor dit account.',
        'X',
        {
          duration: 5000,
        },
      );
      return;
    }

    this.sendingReset.set(true);

    try {
      authInstance.languageCode = 'nl';
      await sendPasswordResetEmail(authInstance, email);
      this.snackBar.open(
        'Er wordt een e-mail verstuurd waarmee u uw wachtwoord opnieuw kunt instellen.',
        'X',
        { duration: 5000 },
      );
    } catch (error) {
      console.error('Fout bij versturen wachtwoord-reset:', error);
      this.snackBar.open('Er is een fout opgetreden. Probeer het later opnieuw.', 'X', {
        duration: 5000,
      });
    } finally {
      this.sendingReset.set(false);
    }
  }
}
